import {
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Switch,
} from 'platform/components';
import {Grid, HStack, Integer, Link, Right, Space, Text, VStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';
import {object, string} from 'yup';

import {always, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetMyWebExtraLinkQuery, useMyWebPublishingOnTenantMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {useBranchId} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {Nullish} from 'shared/web';

import {PlatformAuthenticationFormData} from './PlatformAuthenticationForm';

type PlatformAuthenticationFormMyWebProps = {
  values: PlatformAuthenticationFormData | Nullish;
  isPublishingOnTenant: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onSubmit: FormSubmitHandler<PlatformAuthenticationFormData>;
};

export function PlatformAuthenticationFormMyWeb(props: PlatformAuthenticationFormMyWebProps) {
  const branchId = useBranchId();
  const {data: extraLink} = useGetMyWebExtraLinkQuery({
    branchId: isNil(branchId) || props.isPublishingOnTenant ? undefined : branchId,
  });
  const [setPublishOnTenant, {isLoading: isPublishingOnTenantFetching}] =
    useMyWebPublishingOnTenantMutation();

  const onPublishOnTenantChange = (isEnabled: boolean) =>
    isNotNil(branchId) && setPublishOnTenant({isEnabled, branchId}).unwrap().catch(handleApiError);

  const mode = match([isNil(branchId), props.isPublishingOnTenant])
    .with([false, true], always('Tenant'))
    .with([false, false], always('Branch'))
    .otherwise(always(''));

  const values: PlatformAuthenticationFormData = {
    username: props.values?.username ?? '',
    password: props.values?.password ?? '',
  };

  return (
    <Card title={i18n.t('general.labels.authentication')}>
      <Text size="small" color="secondary">
        {i18n.t('page.settings.labels.authenticationDescription')}
      </Text>
      <Space vertical={4} />
      <Form<PlatformAuthenticationFormData>
        values={values}
        schema={schema}
        onSubmit={props.onSubmit}
        key={mode}
        shouldWatchForUnsavedChanges
      >
        {(control) => (
          <DataStatus
            isLoading={props.isFetching}
            isError={props.isError}
            minHeight={(43 - (isNil(branchId) ? 9 : 0) - (isNil(extraLink) ? 9 : 0)) as Integer}
          >
            <VStack spacing={4}>
              <Show when={isNotNil(branchId)}>
                <Switch
                  label={i18n.t('page.settings.labels.publishOnTenant')}
                  value={props.isPublishingOnTenant}
                  isLoading={isPublishingOnTenantFetching}
                  onChange={onPublishOnTenantChange}
                />
              </Show>
              <Grid columns={2}>
                <FormField
                  control={control}
                  name="username"
                  type="text"
                  label={i18n.t(`page.advertisement.labels.username${mode}`)}
                />
                <FormField
                  control={control}
                  name="password"
                  type="password"
                  label={i18n.t(`page.advertisement.labels.password${mode}`)}
                />
              </Grid>
              <Show when={extraLink}>
                {(extraLink) => (
                  <>
                    <HStack spacing={1} align="center">
                      <Text size="xSmall">{i18n.t('general.labels.URL')}</Text>
                      <Link
                        size="small"
                        data-testid={testIds.settings.advertisingPlatforms('externalLink-myweb')}
                        href={extraLink.link}
                        title={i18n.t('page.advertisement.labels.extraLinkText')}
                      />
                    </HStack>
                  </>
                )}
              </Show>
              <Right>
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.save')}
                />
              </Right>
            </VStack>
          </DataStatus>
        )}
      </Form>
    </Card>
  );
}

const schema = object({
  username: string().nullable().required(),
  password: string().nullable().required(),
});
